import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Mail, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { requisitarAtivacao } from '@/hooks/useAtivacaoLinhas';

type Pessoa = { nome?: string | null; email: string };
interface EmailTroca {
  id: string; assunto: string; de_email: string; para: Pessoa[] | null; cc: Pessoa[] | null;
  enviado_em: string; texto: string | null; reconhecida?: boolean; ao_setor?: boolean;
}
export interface EmailsDaTroca {
  solicitacao: { status: string; gerente_email: string; encaminhada_em?: string | null; cobrancas?: number | null } | null;
  enviado: EmailTroca | null;
  retornos: EmailTroca[];
  iccid?: string | null;
}

const quando = (v: string) => new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const pessoas = (lista: Pessoa[] | null) => (lista || []).map(p => p.email).join(', ') || '—';

/**
 * Os e-mails da troca de chip de UMA linha, no modal do chip (Douglas
 * 24/09/2026: "assim eu tenho como verificar se o e-mail estava certo mesmo e
 * se foi enviada a resposta dela"). O que enviamos à gerente e cada resposta
 * dela, com a marca de qual foi reconhecida como prova e qual foi ao setor.
 */
export function EmailsTrocaDialog({ linhaId, revisao, telefone, aberto, fechar }: {
  linhaId: string; revisao: number; telefone: string; aberto: boolean; fechar: () => void;
}) {
  const [escolhido, setEscolhido] = useState('');
  const dados = useQuery({ queryKey: ['ativacao-retornos-troca', linhaId, revisao], enabled: aberto,
    queryFn: () => requisitarAtivacao<EmailsDaTroca>(`linhas/${linhaId}/retornos-troca`) });
  const lista = [
    ...(dados.data?.enviado ? [{ ...dados.data.enviado, papel: 'Enviado à gerente' }] : []),
    ...[...(dados.data?.retornos || [])].reverse().map(r => ({ ...r, papel: 'Resposta da gerente' })),
  ];
  const aberta = lista.find(e => e.id === escolhido) || lista[0];

  return <Dialog open={aberto} onOpenChange={o => { if (!o) fechar(); }}>
    <DialogContent className="max-w-2xl gap-3">
      <DialogHeader>
        <DialogTitle className="text-[15px]">E-mails da troca · {telefone}</DialogTitle>
        <DialogDescription className="text-[11.5px]">
          {dados.data?.iccid ? <>ICCID <span className="font-mono">{dados.data.iccid}</span> · </> : null}
          {dados.data?.solicitacao ? `gerente ${dados.data.solicitacao.gerente_email}` : 'sem solicitação de troca nesta revisão'}
        </DialogDescription>
      </DialogHeader>

      {dados.isPending ? <p className="text-xs text-muted-foreground">Carregando e-mails…</p>
        : dados.error ? <p role="alert" className="text-xs text-destructive">{dados.error.message}</p>
        : !lista.length ? <p className="text-xs text-muted-foreground">Nenhum e-mail desta troca no espelho da caixa. Se o envio foi recente, aguarde alguns minutos.</p>
        : <>
          <div className="overflow-hidden rounded-lg border border-border-warm">
            {lista.map(e => <button key={`${e.papel}:${e.id}`} type="button" onClick={() => setEscolhido(e.id)}
              className={`flex h-11 w-full items-center gap-2 border-t border-border-warm px-3 text-left first:border-t-0 hover:bg-[#F4F0EA] focus-visible:ring-2 focus-visible:ring-ring dark:hover:bg-[#2A1F1F] ${aberta?.id === e.id ? 'bg-muted' : ''}`}>
              {e.papel === 'Enviado à gerente' ? <Send className="h-3.5 w-3.5 shrink-0 text-primary" /> : <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12.5px] font-semibold">{e.papel}</span>
                <span className="block truncate text-[10.5px] text-muted-foreground" title={e.assunto}>{e.assunto}</span>
              </span>
              {e.ao_setor && <span className="shrink-0 text-[10.5px] text-muted-foreground" title="Encaminhado a trocadechip.pme@claroatendimento.com.br">foi ao setor</span>}
              {e.reconhecida && <span className="inline-flex shrink-0 items-center gap-0.5 text-[10.5px] font-semibold text-[#075E54] dark:text-[#25D366]">
                <Check className="h-3 w-3" />reconhecida</span>}
              <span className="w-[74px] shrink-0 text-right text-[10.5px] tabular-nums text-muted-foreground">{quando(e.enviado_em)}</span>
            </button>)}
          </div>

          {aberta && <div className="rounded-lg border border-border-warm">
            <dl className="grid grid-cols-[52px_minmax(0,1fr)] gap-x-2 gap-y-0.5 bg-[#FAF8F4] px-3 py-2 text-[11px] dark:bg-[#241A1A]">
              <dt className="text-muted-foreground">De</dt><dd className="truncate">{aberta.de_email}</dd>
              <dt className="text-muted-foreground">Para</dt><dd className="truncate" title={pessoas(aberta.para)}>{pessoas(aberta.para)}</dd>
              <dt className="text-muted-foreground">Cópia</dt><dd className="truncate" title={pessoas(aberta.cc)}>{pessoas(aberta.cc)}</dd>
              <dt className="text-muted-foreground">Data</dt><dd>{new Date(aberta.enviado_em).toLocaleString('pt-BR')}</dd>
              <dt className="text-muted-foreground">Assunto</dt><dd className="truncate" title={aberta.assunto}>{aberta.assunto}</dd>
            </dl>
            <p className="max-h-[42vh] overflow-auto whitespace-pre-wrap border-t border-border-warm px-3 py-2 text-[12px] leading-relaxed">
              {aberta.texto?.trim() || 'E-mail sem texto (só imagem ou assinatura).'}</p>
          </div>}
        </>}
    </DialogContent>
  </Dialog>;
}
